import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { userActionPut } from "../utils/axios";
import { UserProfile } from "../models/UserProfile";
import { setMessagingEnabled, setMooLoansEnabled } from "./uiSlice";

export interface KycLinkDto {
    id: string; 
    kycLinkId: string;
    url: string;
    tosUrl: string;
    expiresAt: string;
    fullName: string;
    email: string;
    type: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    userId: number;
}

interface UserState {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    walletAddress?: string;
    privateKey?: string;
    publicKey?: string;
    loading?: boolean;
    error?: ApiError | null;
    userExists?: boolean;
    tenancyName?: string;
    tenantId?: number;
    receiversPublicKey: string;
    user?: UserProfile;
    fcmToken?: string;
    kycLink?: KycLinkDto;
    isLoadingKycLink: boolean;
    gamingCode?: string;
}

export interface ApiError {
    message: string;
    code: string;
}

interface UserTenancySearchResult {
    tenancyName: string;
    tenantId: number;
    userFound: boolean;
}

const initialState: UserState = {
    id: '',
    name: '',
    email: '',
    avatar: '',
    walletAddress: '',
    privateKey: '',
    publicKey: '',
    loading: false,
    error: null,
    userExists: false,
    tenancyName: '',
    tenantId: 0,
    receiversPublicKey: '',
    user: undefined,
    fcmToken: '', 
    isLoadingKycLink: false,
    gamingCode: '',
};

export const getUsersTenancyName = createAsyncThunk(
    "user/getUsersTenancyName",
    async (payload: { id: string }, { rejectWithValue }) => {
        try {
            const { id } = payload;
            const response = await axios.get<{ result: UserTenancySearchResult }>(
                "api/services/app/User/GetUsersTenancyName",
                { params: { id } }
            );

            const data = response.data.result;
            
            if (!data.userFound) {
                return rejectWithValue({
                    message: "User not found",
                    code: "USER_NOT_FOUND"
                });
            }

            return data;
        } catch (error) {
            // if (axios.isAxiosError(error)) {
            //     const serverMessage = error.response?.data?.error?.message;
            //     const statusCode = error.response?.status;
            //     return rejectWithValue({
            //         message: serverMessage || error.message,
            //         code: statusCode ? `HTTP_${statusCode}` : "NETWORK_ERROR"
            //     });
            // }
            return rejectWithValue({
                message: "Unknown error occurred",
                code: "UNKNOWN_ERROR"
            });
        }
    }
);

export const registerDevice = createAsyncThunk(
    "user/registerDevice",
    async (payload: { fcmToken: string; deviceType: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post("api/services/app/FcmDevice/RegisterDevice", payload);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                message: "Failed to register device",
                code: "DEVICE_REGISTRATION_FAILED"
            });
        }
    });

export const getReceiversPublicKey = createAsyncThunk(
    "user/getReceiversPublicKey",
    async (payload: { receiverId: string }, { rejectWithValue }) => {
        try {
            const response = await axios.get("api/services/app/User/GetUsersPublicKey", {
                params: { walletAddress: payload.receiverId }
            });

            // console.log("Receiver's public key response:", response);

            if (response.data.result ==='') {
                return rejectWithValue({
                    message: "Receiver's public key not found",
                    code: "RECEIVER_PUBLIC_KEY_NOT_FOUND"
                });
            }

            return response.data.result;
        } catch (error) {
            return rejectWithValue({
                message: "Failed to get receiver's public key",
                code: "RECEIVER_PUBLIC_KEY_FAILED"
            });
        }
    });



export const getCurrentUserKycLink = createAsyncThunk(
    "kyc/getCurrentUserKycLink",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get("api/services/app/KycLink/GetCurrentUsersKycLink");

            if (!response.data.result) {
                return rejectWithValue({
                    message: "KYC link not found for current user",
                    code: "KYC_LINK_NOT_FOUND"
                });
            }

            // Parse date strings to Date objects
            const kycLink = response.data.result;
            return {
                ...kycLink,
                // expiresAt: new Date(kycLink.expiresAt),
                // createdAt: new Date(kycLink.createdAt),
                // updatedAt: new Date(kycLink.updatedAt)
            } as KycLinkDto;
            
        } catch (error: any) {
            // Handle specific HTTP errors if needed
            if (error.response?.status === 404) {
                return rejectWithValue({
                    message: "KYC link not found for current user",
                    code: "KYC_LINK_NOT_FOUND"
                });
            }
            
            return rejectWithValue({
                message: "Failed to get KYC link",
                code: "KYC_LINK_FAILED",
                details: error.response?.data?.error?.message || error.message
            });
        }
    }
);

  export   const updateUserDetails = createAsyncThunk(
  "user/updateUserDetails",
  async (payload:UserProfile,{dispatch}) => {
    const response = await userActionPut("api/services/app/User/UpdateMe", payload);
    await dispatch(getUserInfo());
    return response.data.result;
  }
);

export const updateUserPassword = createAsyncThunk(
    "user/updateUserPassword",
    async (payload: { currentPassword: string; newPassword: string }, { rejectWithValue, dispatch }) => {
        try {
        const response = await axios.post("api/services/app/User/ChangePassword", payload);
        dispatch(setLoading(false));
        return response.data.result;
        } catch (error) {
        dispatch(setLoading(false));
        return rejectWithValue({
            message: "Failed to change password",
            code: "PASSWORD_CHANGE_FAILED"
        });
        }
    }
    );

export const setUserMessagingPreferences =  createAsyncThunk(
    "user/toggleUserMessagingPreferences",
    async (payload: { enableMessaging: boolean }, { rejectWithValue, dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.post("api/services/app/User/SetMessagingPreferences", {id:payload.enableMessaging});
            dispatch(setMessagingEnabled(payload.enableMessaging));
            dispatch(setLoading(false));
            return response.data.result;
        } catch (error) {
            dispatch(setLoading(false));
            return rejectWithValue({
                message: "Failed to update messaging preferences",
                code: "MESSAGING_PREFERENCES_UPDATE_FAILED"
            });
        }
    }
);

//setMooloansPreferences

export const setMooloansPreferences =  createAsyncThunk(
    "user/toggleMooloansPreferences",
    async (payload: { enableMooLoans: boolean }, { rejectWithValue, dispatch }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.post("api/services/app/User/SetMooLoansPreferences", {id:payload.enableMooLoans});
            dispatch(setMessagingEnabled(payload.enableMooLoans));
            dispatch(setLoading(false));
            return response.data.result;
        } catch (error) {
            dispatch(setLoading(false));
            return rejectWithValue({
                message: "Failed to update moo loans preferences",
                code: "MOO_LOANS_PREFERENCES_UPDATE_FAILED"
            });
        }
    }
);


export const getUserInfo = createAsyncThunk("user/getUserInfo", async (_,{rejectWithValue,dispatch}) => {
    try{
        const response = await axios.post("api/services/app/User/me");
        const userInfo:any = response.data.result; 
        
        dispatch(setMessagingEnabled(userInfo.prefIsMessagingEnabled));
        dispatch(setMooLoansEnabled(userInfo.prefIsMooLoansEnabled));
        return userInfo;
    }catch (error) {
        return rejectWithValue({
            message: "Failed to get user info",
            code: "USER_INFO_FAILED"
        });
    }
  
});

export const updateUserGamingCode = createAsyncThunk(
    "user/updateUserPassword",
    async (payload: { code: string; }, { rejectWithValue, dispatch }) => {
        try {
        const response = await axios.post("api/services/app/User/SetGameCode", payload);
        dispatch(setLoading(false));
        return response.data.result;
        } catch (error) {
        dispatch(setLoading(false));
        return rejectWithValue({
            message: "Failed to update gaming code",
            code: "GAMING_CODE_UPDATE_FAILED"
        });
        }
    }
    );

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUser: (state: UserState, action: { payload: UserState }) => {
            state.id = action.payload.id;
            state.name = action.payload.name;
            state.email = action.payload.email;
            state.avatar = action.payload.avatar;
            state.walletAddress = action.payload.walletAddress;
            state.privateKey = action.payload.privateKey;
            state.publicKey = action.payload.publicKey;
        },
        clearUser: (state: UserState) => {
            state.id = '';
            state.name = '';
            state.email = '';
            state.avatar = '';
            state.walletAddress = '';
            state.privateKey = '';
            state.publicKey = '';
        },
        setWalletAddress: (state: UserState, action: { payload: string }) => {
            if (state.user) {
                state.user.walletAddress = action.payload;
            }
        },
        saveFcmToken: (state: UserState, action: { payload: string }) => {
            state.fcmToken = action.payload;
        },
        setLoading: (state: UserState, action: { payload: boolean }) => {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUsersTenancyName.pending, (state:UserState) => {
                // Handle loading state if needed
                state.loading = true;
                state.error = null;
                state.userExists = false;
            })
            .addCase(getUsersTenancyName.fulfilled, (state, action) => {
                // Handle success state if needed
                state.loading = false;
                state.userExists = action.payload.userFound;
                state.tenancyName = action.payload.tenancyName;
                state.tenantId = action.payload.tenantId;
                state.error = null;
                localStorage.setItem("tenancyName", action.payload.tenancyName);
                localStorage.setItem("tenantId", action.payload.tenantId.toString());
            })
            .addCase(getUsersTenancyName.rejected, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    const { code, message } = action.payload as ApiError;
                    if (code !== "USER_NOT_FOUND") {
                        state.error = { code, message };
                    }
                    
                } else {
                    state.error = {
                        code: "UNKNOWN_ERROR",
                        message: action.error.message || "Unknown error occurred"
                    };
                }
            }).addCase(registerDevice.pending, (state:UserState) => {
                // Handle loading state if needed
                state.loading = true;
                state.error = null;
            })
            .addCase(registerDevice.fulfilled, (state, action) => {
                // Handle success state if needed
                state.loading = false;
                state.error = null;
            })
            .addCase(registerDevice.rejected, (state, action) => {      
                state.loading = false;
                if (action.payload) {
                    const { code, message } = action.payload as ApiError;
                    state.error = { code, message };
                } else {
                    state.error = {
                        code: "UNKNOWN_ERROR",
                        message: action.error.message || "Unknown error occurred"
                    };
                }
            }).addCase(getReceiversPublicKey.pending, (state:UserState) => {
                // Handle loading state if needed
                state.loading = true;
                state.error = null;
            })
            .addCase(getReceiversPublicKey.fulfilled, (state, action) => {                
                state.loading = false;                
                state.receiversPublicKey = action.payload;
                state.error = null;
            })
            .addCase(getReceiversPublicKey.rejected, (state, action) => {   
                state.loading = false;
                if (action.payload) {
                    const { code, message } = action.payload as ApiError;
                    state.error = { code, message };
                } else {
                    state.error = {
                        code: "UNKNOWN_ERROR",
                        message: action.error.message || "Unknown error occurred"
                    };
                }
            }).addCase(updateUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        //state.user = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateUserDetails.rejected, (state, action) => {
        //state.error = action.error.message;
        state.loading = false;
      }).addCase(getUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.error = {
          code: action.error.code || "UNKNOWN_ERROR",
          message: action.error.message || "Unknown error occurred"
        };
        state.loading = false;
      }).addCase(getCurrentUserKycLink.pending, (state) => {
        state.isLoadingKycLink = true;
        state.error = null;
      })
      .addCase(getCurrentUserKycLink.fulfilled, (state, action) => {
        state.kycLink = action.payload;
        state.isLoadingKycLink = false;
        state.error = null;
      })
      .addCase(getCurrentUserKycLink.rejected, (state, action) => {
        state.error = {
          code: action.error.code || "UNKNOWN_ERROR",
          message: action.error.message || "Unknown error occurred"
        };
        state.isLoadingKycLink = false;
    }).addCase(updateUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.error = {
            code: action.error.code || "UNKNOWN_ERROR",
            message: action.error.message || "Unknown error occurred"
        };
        state.loading = false;
      })
    }
});

export const { setUser, clearUser, setWalletAddress, saveFcmToken,setLoading} = userSlice.actions;
export default userSlice.reducer;